/**
 * How a dataset-write node addresses the row it writes.
 *
 * Not a field on the tool. The tool decides between the two on what identifies
 * the row: `keyColumns`, or a `rowKey` on the row itself, mean the write goes
 * into one that already exists, and neither means it appends. So the mode is a
 * property of the editor, recovered from the saved input rather than stored
 * alongside it — nothing extra is sent.
 */
export type WriteMode = 'create' | 'update';

/** Column name to the template that produces its value. */
export type DraftValues = Readonly<Record<string, string>>;

export type SerializedRow = {
  /**
   * A template producing the row's declared key, or null when the row is found
   * by key columns or appended.
   *
   * Written to `rowKey` rather than `rowId`: `rowKey` resolves the value through
   * the dataset's declared key columns, so it names the right row whether the
   * dataset is keyed by a business column or by its internal row number — the
   * shape a dataset-trigger write-back needs, and the one
   * `migrateDatasetWriteRowKeyParams` moves a legacy `rowId` binding into. Only
   * ever bound to the triggering row's own declared key — see `RowIdentity` in
   * `dataset-write.control`.
   */
  readonly rowKey: string | null;
  readonly values: Record<string, string>;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/**
 * Reads the saved `rows` param back into one row's column templates.
 *
 * A node writes a single row: it runs once per thing that triggered it, and the
 * row it writes is that thing. Anything beyond the first saved row is dropped
 * rather than silently rewritten, so a payload authored elsewhere degrades to
 * something this editor can honestly represent.
 */
export const parseValues = (value: unknown): DraftValues => {
  if (!Array.isArray(value)) {
    return {};
  }
  const first = asRecord(value[0]);
  const values = asRecord(first?.['values']) ?? {};
  return Object.fromEntries(
    Object.entries(values).map(([column, cell]) => [
      column,
      cell === null ? '' : String(cell),
    ])
  );
};

/**
 * The saved row-key template, or `''` when the row is not addressed by one.
 *
 * Read from `rowKey`, where a dataset-trigger write-back's identifier now lives
 * (see `migrateDatasetWriteRowKeyParams`, which moves a legacy `rowId` binding
 * there whenever the workflow is opened). A node that has not been migrated yet
 * — or one addressing an existing internal id — still carries it under `rowId`,
 * so that is read as a fallback: either keeps the node reading as an update
 * rather than springing back to "Add a row" the next time the drawer opens,
 * which is exactly what reading only one of the two did.
 *
 * A string rather than `string | null` so it compares directly against the
 * template the control would write — the check that stops the effect keeping
 * the key current from committing on every render.
 */
export const parseRowKey = (value: unknown): string => {
  if (!Array.isArray(value)) {
    return '';
  }
  const first = asRecord(value[0]);
  const rowKey = first?.['rowKey'];
  if (typeof rowKey === 'string' && rowKey !== '') {
    return rowKey;
  }
  const rowId = first?.['rowId'];
  return typeof rowId === 'string' ? rowId : '';
};

/** The sibling param naming the columns that identify a row. */
export const readKeyColumns = (
  params: Readonly<Record<string, unknown>>
): string[] => {
  const raw = params['keyColumns'];
  return Array.isArray(raw)
    ? raw.filter((name): name is string => typeof name === 'string')
    : [];
};

/**
 * Naming something that identifies the row is what makes a write an update, so
 * the saved input already says which mode it is in and nothing has to be
 * remembered separately.
 *
 * Both ways of naming one count. Reading only `keyColumns` made a node that
 * addresses its row by key read back as an append, and the toggle would spring
 * to "Add a row" the next time the drawer opened. `rowKey` (see `parseRowKey`)
 * is the second way, and must be read here too: the migration that moves a
 * trigger write-back's identifier from `rowId` to `rowKey` on open otherwise
 * left every such node reading as an append.
 *
 * A node with none of the three — nothing to match on and nothing written —
 * has not been configured at all, and opens on "Update a row". Reading that as
 * an append made the destructive mode the default, and on a dataset whose key
 * the editor can fill in from the trigger it is also the one that needs no
 * further input.
 */
export const deriveMode = (
  keyColumns: readonly string[],
  rowKey = '',
  values: DraftValues = {}
): WriteMode => {
  if (keyColumns.length > 0 || rowKey !== '') {
    return 'update';
  }
  return Object.values(values).some((template) => template !== '')
    ? 'create'
    : 'update';
};

/**
 * The shape the tool takes.
 *
 * A column left blank is omitted rather than written as an empty string: the
 * two mean different things to a dataset, and "I did not fill this in" is not a
 * request to erase what is there.
 *
 * No filled columns means no row at all, whatever `rowKey` says: a row naming
 * only the key it writes into is a write with nothing to write, which the tool
 * rejects. Emitting nothing keeps the node visibly incomplete instead.
 */
export const serializeValues = (
  values: DraftValues,
  rowKey: string | null = null
): SerializedRow[] => {
  const filled = Object.entries(values).filter(
    ([column, template]) => column !== '' && template !== ''
  );
  return filled.length === 0
    ? []
    : [{ rowKey, values: Object.fromEntries(filled) }];
};
